import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Minus, Flag, Lock } from "@phosphor-icons/react";
import { SiteNav } from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

const INK = '#2A1E5C';
const INK2 = '#5D5A6B';
const GOLD = '#F5CB5C';
const GREEN = '#3E8E4E';
const CREAM = '#FDFBF7';
const PAPER = '#FAF1D8';

const COMPARISON = [
  {
    tool: 'Payday Relay', us: true,
    best: 'ADHD entrepreneurs, 1099 earners, freelancers, creators, contractors, platform workers',
    job: 'Turns income tracking into a weekly race with targets, streaks, banks, and a 52-week season',
    limit: 'Newer product, built around one specific method',
  },
  {
    tool: 'YNAB',
    best: 'People who want a hands-on zero-based budget',
    job: 'Assigns dollars to jobs and helps users plan spending',
    limit: 'Strong budgeting method, but not designed around workday boundaries or income pacing',
  },
  {
    tool: 'QuickBooks Solopreneur / Self-Employed-style tools',
    best: 'Freelancers who need bookkeeping, expenses, invoices, and tax prep support',
    job: 'Tracks business income, expenses, deductions, and tax records',
    limit: 'Strong admin tool, but not a daily motivational system',
  },
  {
    tool: 'Stride / tax deduction apps',
    best: 'Gig workers and contractors tracking mileage, expenses, and deductions',
    job: 'Helps capture tax-related deductions and work expenses',
    limit: 'Useful for deductions, but not built to pace weekly earning behavior',
  },
  {
    tool: 'FreshBooks / invoicing tools',
    best: 'Service providers billing clients',
    job: 'Handles invoices, payments, expenses, and client records',
    limit: 'Good for business admin, but not built as an income habit system',
  },
  {
    tool: 'Google Sheets / budget templates',
    best: 'DIY users who like full control',
    job: 'Stores custom income, budget, and tracking formulas',
    limit: 'Flexible, but easy to abandon without a daily app loop keeping it filled in',
  },
  {
    tool: 'Traditional budgeting apps',
    best: 'W-2 earners with predictable paycheck cycles',
    job: 'Categorizes spending and tracks account balances',
    limit: 'Usually assumes money arrives on a built-in schedule',
  },
];

const MATRIX_COLS = ['Payday Relay', 'YNAB', 'QuickBooks-style tools', 'Stride-style tools', 'Google Sheets'];

const MATRIX = [
  ['May qualify as a deductible business expense\u00B9', 'Yes', 'Partial', 'Yes', 'Yes', 'Partial'],
  ['Data Ownership\u00B2', 'Yes', 'No', 'No', 'No', 'Yes (manual)'],
  ['Built for unstructured income', 'Yes', 'Partial', 'Partial', 'Partial', 'Manual'],
  ['Designed for ADHD brains', 'Yes', 'No', 'No', 'No', 'Manual'],
  ['Weekly income target', 'Yes', 'Manual', 'No', 'No', 'Manual'],
  ['Daily income logging', 'Yes', 'Manual', 'Yes', 'Yes', 'Manual'],
  ['Clear "you\u2019ve done enough" signal', 'Yes', 'No', 'No', 'No', 'Manual'],
  ['Streaks and progress rewards', 'Yes', 'No', 'No', 'No', 'Manual'],
  ['Tax cushion support', 'Yes', 'Manual', 'Yes', 'Partial', 'Manual'],
  ['PTO / time-off bank', 'Yes', 'Manual', 'No', 'No', 'Manual'],
  ['Google Sheet that auto-syncs', 'Yes', 'No', 'Export only', 'Export only', 'Manual'],
  ['Built as a game', 'Yes', 'No', 'No', 'No', 'No'],
];

const Verdict = ({ v }) => {
  if (v === 'Yes') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold whitespace-nowrap" style={{ color: GREEN }}>
        <Check className="w-4 h-4 flex-shrink-0" strokeWidth={3} /> Yes
      </span>
    );
  }
  if (v === 'No' || v === 'Low') {
    return (
      <span className="inline-flex items-center gap-1 text-xs whitespace-nowrap" style={{ color: '#A79D89' }}>
        <Minus className="w-4 h-4 flex-shrink-0" /> {v}
      </span>
    );
  }
  return <span className="text-xs font-medium" style={{ color: '#C9861B' }}>{v}</span>;
};

const StartSprintButton = ({ testId, className = '' }) => (
  <Link
    to="/start/free-sprint"
    className={`inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-base sm:text-lg transition-transform hover:scale-[1.03] shadow-lg ${className}`}
    style={{ background: GOLD, color: INK }}
    data-testid={testId}
  >
    Start Free Sprint <ArrowRight className="w-5 h-5" />
  </Link>
);

export default function ComparePage() {
  useEffect(() => {
    document.title = 'Payday Relay vs. Other Money Tools';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Payday Relay vs. YNAB, QuickBooks, Stride, FreshBooks, Google Sheets, and freelancer budgeting apps. Most money tools track what happened. Payday Relay gives unstructured income a weekly finish line.'
      );
    }
  }, []);

  return (
    <div className="min-h-screen pb-24 sm:pb-0" style={{ background: CREAM }} data-testid="compare-page">
      <SiteNav />

      {/* ============ HERO ============ */}
      <section className="pt-14 sm:pt-20 pb-14 sm:pb-16 px-5 sm:px-8" style={{ background: INK }} data-testid="compare-hero">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>
            Honest comparison
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-6" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#FFFFFF' }} data-testid="compare-headline">
            Payday Relay vs. budgeting apps, spreadsheets, and freelancer finance tools
          </h1>
          <p className="text-base sm:text-lg leading-relaxed mb-4" style={{ color: 'rgba(255,255,255,0.82)' }}>
            Payday Relay is built for ADHD brains, 1099 earners, freelancers, contractors, creators,
            platform workers, and self-employed people with unstructured income.
          </p>
          <p className="text-lg sm:text-xl leading-relaxed mb-9 font-medium" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#FFFFFF' }}>
            Most money tools help you organize what happened.{' '}
            <span style={{ color: GOLD }}>Payday Relay helps you decide when enough is enough for the week.</span>
          </p>
          <StartSprintButton testId="compare-hero-cta" />
        </div>
      </section>

      {/* ============ WHO EACH TOOL IS FOR ============ */}
      <section className="py-14 sm:py-20 px-5 sm:px-8" data-testid="compare-table-section">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#C9861B' }}>
            The short version
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl leading-snug mb-4" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
            Who each tool is really for
          </h2>
          <p className="text-base sm:text-lg leading-relaxed mb-10 max-w-3xl" style={{ color: INK2 }}>
            Every tool below does a real job well. Start here to find the one whose job matches yours. 
            then scroll down for the feature-by-feature checklist.
          </p>

          {/* Mobile: one card per tool (no sideways swiping) */}
          <div className="space-y-4 sm:hidden">
            {COMPARISON.map((row, i) => (
              <div
                key={row.tool}
                className="rounded-2xl p-5"
                style={{
                  background: row.us ? '#FBF0CE' : '#FFFFFF',
                  border: `1px solid ${row.us ? GOLD : 'rgba(42,30,92,0.12)'}`,
                  borderLeft: `5px solid ${row.us ? GOLD : 'rgba(42,30,92,0.14)'}`,
                  boxShadow: '0 8px 22px rgba(42,30,92,0.06)',
                }}
                data-testid={`compare-card-${i}`}
              >
                <h3 className="text-lg leading-snug mb-4 flex items-start gap-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
                  {row.us && <Flag className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: '#C9861B' }} />}
                  {row.tool}
                </h3>
                {[['Best for', row.best], ['The core job', row.job], ['Main limitation', row.limit]].map(([label, value]) => (
                  <div key={label} className="mb-3.5 last:mb-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] mb-1" style={{ color: '#A79D89' }}>
                      {label}
                    </p>
                    <p className="text-sm leading-relaxed" style={{ color: INK2 }}>{value}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Desktop: four roomy columns, no scrolling */}
          <div className="hidden sm:block rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(42,30,92,0.12)', boxShadow: '0 14px 36px rgba(42,30,92,0.08)' }}>
            <table className="w-full text-left" style={{ borderCollapse: 'separate', borderSpacing: 0 }} data-testid="compare-main-table">
              <thead>
                <tr>
                  {['Tool', 'Best for', 'The core job', 'Main limitation'].map((h, i) => (
                    <th
                      key={h}
                      className="px-6 py-5 text-xs font-bold uppercase tracking-[0.14em] align-bottom"
                      style={{ background: INK, color: i === 0 ? GOLD : '#FFFFFF', width: i === 0 ? '22%' : '26%' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={row.tool} data-testid={`compare-row-${i}`}>
                    <th
                      scope="row"
                      className="px-6 py-7 text-base font-bold align-top"
                      style={{
                        background: row.us ? '#FBF0CE' : i % 2 ? '#FFFFFF' : '#FAF8F3',
                        color: INK,
                        borderTop: '1px solid rgba(42,30,92,0.08)',
                        borderRight: `3px solid ${row.us ? GOLD : 'rgba(42,30,92,0.06)'}`,
                        fontFamily: "'Fraunces', Georgia, serif",
                        lineHeight: 1.35,
                      }}
                    >
                      {row.us && <Flag className="w-4 h-4 inline mr-1.5 -mt-1" style={{ color: '#C9861B' }} />}
                      {row.tool}
                    </th>
                    {[row.best, row.job, row.limit].map((cell, ci) => (
                      <td
                        key={ci}
                        className="px-6 py-7 text-sm leading-relaxed align-top"
                        style={{
                          background: row.us ? 'rgba(245,203,92,0.13)' : i % 2 ? '#FFFFFF' : '#FAF8F3',
                          color: row.us ? INK : INK2,
                          borderTop: '1px solid rgba(42,30,92,0.08)',
                        }}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ============ FEATURE MATRIX ============ */}
      <section className="py-14 sm:py-20 px-4 sm:px-8" style={{ background: PAPER }} data-testid="compare-matrix-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
            Side-By-Side At A Glance
          </h2>
          <p className="text-sm mb-6" style={{ color: INK2 }}>
            Feature by feature. This is where the difference shows up. "Manual" means you can build it
            yourself if you keep maintaining it.
            <span className="sm:hidden"> Swipe sideways to compare each tool.</span>
          </p>

          <div className="overflow-x-auto rounded-2xl" style={{ border: '1px solid rgba(42,30,92,0.12)', boxShadow: '0 10px 30px rgba(42,30,92,0.08)' }}>
            <table className="w-full text-left" style={{ minWidth: 760, borderCollapse: 'separate', borderSpacing: 0 }} data-testid="compare-matrix-table">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 px-4 py-4 text-[11px] font-bold uppercase tracking-wider" style={{ background: INK, color: '#FFFFFF', minWidth: 230 }}>
                    Feature
                  </th>
                  {MATRIX_COLS.map((c, i) => (
                    <th
                      key={c}
                      className="px-4 py-4 text-[11px] font-bold uppercase tracking-wider"
                      style={{ background: i === 0 ? '#3A2A78' : INK, color: i === 0 ? GOLD : '#FFFFFF', minWidth: 104 }}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MATRIX.map(([feature, ...vals], i) => (
                  <tr key={feature} data-testid={`matrix-row-${i}`}>
                    <th
                      scope="row"
                      className="sticky left-0 z-10 px-4 py-3.5 text-xs font-bold"
                      style={{
                        background: i % 2 ? '#FFFFFF' : '#FDFBF5',
                        color: INK,
                        borderTop: '1px solid rgba(42,30,92,0.08)',
                        borderRight: '3px solid rgba(42,30,92,0.06)',
                      }}
                    >
                      {feature}
                    </th>
                    {vals.map((v, ci) => (
                      <td
                        key={ci}
                        className="px-4 py-3.5"
                        style={{
                          background: ci === 0 ? 'rgba(245,203,92,0.16)' : i % 2 ? '#FFFFFF' : '#FDFBF5',
                          borderTop: '1px solid rgba(42,30,92,0.08)',
                        }}
                      >
                        <Verdict v={v} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div
            className="mt-6 rounded-2xl px-5 py-5 sm:px-6 space-y-4"
            style={{ background: '#FFFFFF', border: '1px solid rgba(42,30,92,0.12)', borderLeft: `5px solid ${GOLD}` }}
            data-testid="matrix-footnote"
          >
            <p className="text-sm sm:text-base leading-relaxed" style={{ color: INK2 }}>
              <sup style={{ color: '#C9861B', fontWeight: 700 }}>1</sup> Payday Relay may qualify as a
              deductible business expense for many self-employed users when used to manage business
              income. Tax treatment depends on your situation, so check with a tax professional.
            </p>
            <p className="text-sm sm:text-base leading-relaxed" style={{ color: INK2 }}>
              <sup style={{ color: '#C9861B', fontWeight: 700 }}>2</sup> Payday Relay runs through a
              Google Sheet in your own Drive, so your income history, targets, and set-asides remain
              accessible even if you cancel the app.
            </p>
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="py-16 sm:py-20 px-5 sm:px-8" style={{ background: INK }} data-testid="compare-cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl mb-5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#FFFFFF' }}>
            Start with four free relays
          </h2>
          <p className="text-base sm:text-lg leading-relaxed mb-4" style={{ color: 'rgba(255,255,255,0.82)' }}>
            Run the 4-Relay Sprint with your own Payday Relay Google Sheet, generated for you at
            sign-up, filled in automatically as you log, and see whether a weekly finish line
            changes the way you earn.
          </p>
          <p className="text-base sm:text-lg leading-relaxed mb-9" style={{ color: 'rgba(255,255,255,0.82)' }}>
            If it helps, keep your numbers, streak, and rhythm alive with the full 52-week Season Pass.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <StartSprintButton testId="compare-bottom-cta" />
            <Link
              to="/features"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-base sm:text-lg border-2 transition-colors hover:bg-white/10"
              style={{ borderColor: 'rgba(255,255,255,0.45)', color: '#FFFFFF' }}
              data-testid="compare-features-link"
            >
              Explore Features <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="flex items-center justify-center gap-2 text-xs mt-7" style={{ color: 'rgba(255,255,255,0.65)' }}>
            <Lock className="w-3.5 h-3.5" style={{ color: GOLD }} /> $0 today. Cancel before day 28 and pay nothing.
          </p>
        </div>
      </section>

      <SiteFooter />

      {/* Sticky mobile CTA */}
      <div
        className="sm:hidden fixed bottom-0 left-0 right-0 z-30 pl-4 pr-20 py-3"
        style={{ background: 'rgba(30, 22, 64, 0.97)', backdropFilter: 'blur(6px)', borderTop: '1px solid rgba(245,203,92,0.35)' }}
      >
        <Link
          to="/start/free-sprint"
          className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full font-bold text-sm shadow-md"
          style={{ background: GOLD, color: INK }}
          data-testid="compare-sticky-cta"
        >
          Start Free Sprint <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
